import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, ReferenceLine, ResponsiveContainer,
  CartesianGrid, XAxis, YAxis, Tooltip as RechartTooltip,
} from 'recharts';
import { PageHeader, StatCard, TierBadge, SubTabBar, fmtDollar, fmtMillions, fmtCount } from './shared';
import { apiFetch } from '../api';

// Colors for the spend-by-tier bar
const SPEND_TIER_COLORS = ['#003c71', '#0067a0', '#00a7b5', '#9ddde7'];

function riskColor(pct) {
  if (pct >= 65) return '#dc3545';
  if (pct >= 50) return '#fd7e14';
  return '#ffc107';
}

function LoadingRow() {
  return <div className="p-4 text-muted">Loading…</div>;
}

function ErrorRow() {
  return <div className="p-4 text-danger">Failed to load dashboard data.</div>;
}

// ── Views ─────────────────────────────────────────────────────────────────────

function StratificationView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/member-intelligence/strat')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Member Intelligence  ·  Population Stratification & Cohort Builder" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header fw-semibold">Risk Stratification Matrix</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Tier</th><th>Members</th><th>% Pop.</th><th>Avg PMPM</th><th>Total Spend</th><th>Priority</th></tr></thead>
                <tbody>
                  {data.riskTiers.map(r => (
                    <tr key={r.tier}>
                      <td className="fw-semibold">{r.tier}</td>
                      <td>{fmtCount(r.members)}</td><td>{r.pct}</td><td>{fmtDollar(r.pmpm)}</td><td>{fmtMillions(r.spend)}</td>
                      <td><TierBadge tier={r.priority} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body pt-2 pb-3">
              <div className="fw-semibold mb-2" style={{ fontSize: '0.78rem', color: 'var(--eh-navy)' }}>Spend Distribution by Tier</div>
              <div className="d-flex rounded overflow-hidden" style={{ height: '36px' }}>
                {data.spendByTier.map((s, i) => (
                  <div key={s.name} title={`${s.name}: ${s.value}%`} style={{
                    flex: s.value,
                    background: SPEND_TIER_COLORS[i],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: i < 2 ? '#fff' : '#003c71',
                    fontSize: '0.7rem', fontWeight: 700, overflow: 'hidden',
                  }}>
                    {s.name} {s.value}%
                  </div>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2 mt-1" style={{ fontSize: '0.72rem' }}>
                {data.spendByTier.map((s, i) => (
                  <span key={s.name}><span style={{ color: SPEND_TIER_COLORS[i], fontWeight: 700 }}>■</span> {s.name} ({s.value}%)</span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header fw-semibold">Cohort Segment Builder</div>
            <div className="card-body">
              <div className="row g-2">
                {data.cohortFilters.map(f => (
                  <div key={f.label} className="col-12 col-sm-6">
                    <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>{f.label}</label>
                    <input className="form-control form-control-sm" readOnly value={f.value} />
                  </div>
                ))}
              </div>
              <div className="mt-3 fw-bold" style={{ color: 'var(--eh-navy)', fontSize: '0.88rem' }}>
                Matched Members: <span style={{ color: 'var(--eh-teal)', fontSize: '1.1rem' }}>{fmtCount(data.matchedCount)}</span>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <button className="btn btn-sm btn-outline-primary">Export List</button>
            <button className="btn btn-sm btn-primary">Send to Outreach</button>
            <button className="btn btn-sm btn-outline-secondary">Build Pathway</button>
          </div>
        </div>
      </div>
    </>
  );
}

function PatientRosterView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/member-intelligence/roster')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Member Intelligence  ·  Patient Level Roster" memberCount={memberCount} />
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--eh-navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{data.provider.initials}</div>
            <div>
              <div className="fw-bold" style={{ color: 'var(--eh-navy)', fontSize: '0.92rem' }}>
                {data.provider.name} · {data.provider.specialty} · {data.provider.market}
              </div>
              <div className="d-flex gap-3 mt-1 flex-wrap" style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)' }}>
                <span>{data.provider.memberCount} Members</span>
                <span>Cost O/E: <strong>{data.provider.costOe}</strong></span>
                <span>Quality O/E: <strong>{data.provider.qualityOe}</strong></span>
                <span>Risk Score: <strong>{data.provider.riskScore}</strong></span>
                <TierBadge tier={data.provider.tier} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-5">
              <input className="form-control form-control-sm" placeholder="🔍  Search members..." />
            </div>
            <div className="col-12 col-md-7">
              <div className="d-flex gap-1 flex-wrap">
                {['All Members', 'High Risk', 'Rising Cost', 'Care Gaps', 'No Recent Visit'].map((tab, i) => (
                  <button key={tab} className={`btn btn-sm ${i === 0 ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ fontSize: '0.75rem' }}>{tab}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header fw-semibold">Member Roster — Sorted by Risk Score</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Member</th><th>Age/Sex</th><th>Risk</th><th>PMPM</th><th>Last Visit</th><th>Next Appt</th><th>Primary Dx</th><th>Gaps</th><th>SDOH</th><th>Trend</th><th>Alert</th></tr>
              </thead>
              <tbody>
                {data.rosterMembers.map(r => (
                  <tr key={r.name}>
                    <td className="fw-semibold">{r.name}</td>
                    <td>{r.age}/{r.sex}</td>
                    <td>
                      <span className="dash-risk-score" style={{ background: r.risk >= 2 ? '#dc3545' : r.risk >= 1.5 ? '#fd7e14' : '#198754' }}>{r.risk}</span>
                    </td>
                    <td>{fmtDollar(r.pmpm)}</td>
                    <td style={{ fontSize: '0.75rem' }}>{r.last}</td>
                    <td style={{ fontSize: '0.75rem', color: r.next === 'Unscheduled' ? '#dc3545' : 'inherit', fontWeight: r.next === 'Unscheduled' ? 600 : 400 }}>{r.next}</td>
                    <td style={{ fontSize: '0.75rem' }}>{r.dx}</td>
                    <td className="text-center">{r.gaps > 0 ? <span className="fw-bold text-danger">{r.gaps}</span> : 0}</td>
                    <td>{r.sdoh !== 'None' ? <span className="dash-flag">{r.sdoh}</span> : <span className="text-muted">—</span>}</td>
                    <td className="text-center">{r.trend}</td>
                    <td><TierBadge tier={r.alert} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer d-flex align-items-center justify-content-between py-2" style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)' }}>
          <span>Showing {data.rosterMembers.length} of {data.provider.memberCount} members · Sorted by: Risk Score (desc)</span>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-secondary py-0">‹ Prev</button>
            <span>Page 1 of {Math.ceil(parseInt(data.provider.memberCount.replace(',', '')) / data.rosterMembers.length)}</span>
            <button className="btn btn-sm btn-outline-secondary py-0">Next ›</button>
          </div>
        </div>
      </div>
    </>
  );
}

function ProfileView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/member-intelligence/profile')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Member Intelligence  ·  360° Member Profile" memberCount={memberCount} />
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--eh-navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{data.member.initials}</div>
            <div>
              <div className="fw-bold" style={{ color: 'var(--eh-navy)', fontSize: '0.95rem' }}>
                {data.member.name} &nbsp;<span style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)', fontWeight: 400 }}>{data.member.id}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)' }}>
                {data.member.demographics} · PCP: {data.member.pcp} · Region: {data.member.region}
              </div>
            </div>
            <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
              {data.member.badges.map(b => (
                <span key={b} className="dash-filter-tag">{b}</span>
              ))}
              <TierBadge tier={data.member.tier} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 1: Risk Score | PMPM Chart | Active Conditions | SDOH Flags */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-3 col-xl-2">
          <div className="card h-100 text-center" style={{ borderTop: '4px solid #dc3545' }}>
            <div className="card-body py-3 px-2">
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--eh-text-mid)' }}>Risk Score</div>
              <div className="mt-1" style={{ lineHeight: 1 }}>
                <span style={{ fontSize: '2.8rem', fontWeight: 800, color: '#dc3545' }}>{data.riskCard.score}</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--eh-text-mid)', marginLeft: 2 }}>/100</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#dc3545', fontWeight: 600, marginTop: 4 }}>{data.riskCard.label}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--eh-text-mid)', marginTop: 2 }}>Prior: {data.riskCard.prior} &nbsp;↑{data.riskCard.priorDelta}</div>
              <span className="badge mt-2 d-inline-block" style={{ background: '#dc3545', fontSize: '0.65rem' }}>{data.riskCard.badge}</span>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-9 col-xl-4">
          <div className="card h-100">
            <div className="card-header fw-semibold" style={{ fontSize: '0.82rem' }}>PMPM Spend — 24 Months</div>
            <div className="card-body py-2">
              <ResponsiveContainer width="100%" height={130}>
                <AreaChart data={data.pmpm24mo} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mo" tick={{ fontSize: 9 }} interval={3} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={v => '$' + Math.round(v).toLocaleString('en-US')} width={45} />
                  <RechartTooltip formatter={v => [fmtDollar(v), 'PMPM']} />
                  <ReferenceLine y={data.pmpmSummary.avgY} stroke="#6c757d" strokeDasharray="4 2" label={{ value: fmtDollar(data.pmpmSummary.avgLabel), position: 'insideTopRight', fontSize: 9, fill: '#6c757d' }} />
                  <Area type="monotone" dataKey="pmpm" stroke="#0067a0" fill="#e8f5fb" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="d-flex gap-4 mt-2" style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)' }}>
                <span>Current: <strong style={{ color: 'var(--eh-navy)' }}>{fmtDollar(data.pmpmSummary.currentLabel)}</strong></span>
                <span>24-mo avg: <strong style={{ color: 'var(--eh-navy)' }}>{fmtDollar(data.pmpmSummary.avgText)}</strong></span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card h-100">
            <div className="card-header fw-semibold">Active Conditions</div>
            <div className="card-body p-0">
              {data.activeConditions.map(([cond, sev]) => (
                <div key={cond} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                  <span style={{ fontSize: '0.82rem' }}>• {cond}</span>
                  <TierBadge tier={sev} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card h-100">
            <div className="card-header fw-semibold">SDOH Flags</div>
            <div className="card-body py-2">
              {data.sdohFlags.map(([flag, active]) => (
                <div key={flag} style={{ fontSize: '0.82rem', color: active ? '#dc3545' : '#198754', padding: '3px 0' }}>{flag}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Quality Gaps | Future Risks — full width */}
      <div className="row g-3">
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header fw-semibold">Quality Gaps & Care Opportunities</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Measure</th><th>Last</th><th>Due</th><th>Status</th><th>Stars</th></tr></thead>
                <tbody>
                  {data.memberGaps.map(r => (
                    <tr key={r.measure}>
                      <td className="fw-semibold" style={{ fontSize: '0.78rem' }}>{r.measure}</td>
                      <td style={{ fontSize: '0.72rem' }}>{r.last}</td>
                      <td style={{ fontSize: '0.72rem', color: r.due === 'Current' ? 'inherit' : '#dc3545' }}>{r.due}</td>
                      <td><span className={`dash-status-badge ${r.status === 'CLOSED' ? 'status-on-track' : 'status-at-risk'}`}>{r.status}</span></td>
                      <td style={{ color: '#f59e0b', fontSize: '0.7rem' }}>{r.stars}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header fw-semibold">Future Event Risk Indicators</div>
            <div className="card-body">
              {data.futureRisks.map(r => (
                <div key={r.label} className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <span style={{ fontSize: '0.8rem' }}>{r.label}</span>
                    <span className="fw-bold" style={{ fontSize: '0.82rem', color: riskColor(r.pct) }}>{r.pct}%</span>
                  </div>
                  <div style={{ height: 6, background: '#e9ecef', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${r.pct}%`, background: riskColor(r.pct), borderRadius: 3 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SUBTABS = [
  { key: 'strat',   label: 'Population Stratification' },
  { key: 'roster',  label: 'Patient Level Roster'      },
  { key: 'profile', label: '360° Member Profile'       },
];

const VIEW_COMPONENTS = {
  strat:   StratificationView,
  roster:  PatientRosterView,
  profile: ProfileView,
};

export default function MemberIntelligence() {
  const [sub, setSub] = useState('strat');
  const [memberCount, setMemberCount] = useState(null);
  const ViewComponent = VIEW_COMPONENTS[sub];
  useEffect(() => {
    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(d => setMemberCount(d.membership_count))
      .catch(() => {});
  }, []);
  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={SUBTABS} active={sub} onChange={setSub} />
      <ViewComponent memberCount={memberCount} />
    </div>
  );
}
